import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Box, MenuItem, Select} from '@mui/material';
import {
    resetUpdatingStatus,
    retrieveEthnicity,
    retrieveEthnicityLookups,
    retrieveProfile,
    updateEthnicity
} from '../../ProfilesSlice';
import {showSnackbar} from '../../../../../AppLayout/ApplicationSlice';
import {useParams} from 'react-router-dom';
import Typography from '@mui/material/Typography';
import PassportFields from './PassportFields';
import {STUDENT_PROFILE_CONFIRMATION_TYPES} from '../../const/StudentProfileConfirmationTypes';
import PropTypes from 'prop-types';
import ConfirmationWrapper from '../../Components/ConfirmationWrapper';
import PROFILE_TYPE from '../../../../../constants/profileTypes';
import {toDateOnly} from '../../../../../utility/dateUtil';
import ContactHelpTip from './ContactHelpTip';

const normaliseOptionTitle = (value = '') => String(value).trim().toLowerCase();

const prioritiseOptions = (options, priorityTitles = []) => {
    if (!priorityTitles.length) {
        return options;
    }

    const priorityTitleSet = new Set(priorityTitles.map(normaliseOptionTitle));
    const prioritised = [];
    const remaining = [];

    options.forEach(option => {
        if (priorityTitleSet.has(normaliseOptionTitle(option.title))) {
            prioritised.push(option);
        } else {
            remaining.push(option);
        }
    });

    return prioritised.length > 0
        ? [...prioritised, ...remaining]
        : options;
};

const buildSelectOptions = (list, priorityTitles = []) => {
    const options = list.map(element => ({
        key: element.code,
        value: element.code,
        title: element.description,
        isActive: element.isActive
    }));

    return prioritiseOptions(options, priorityTitles);
};

const hasMeaningfulValue = value => value !== null && value !== undefined && value !== '';

const hasValidSelectedOption = (value, options = []) => {
    if (!hasMeaningfulValue(value)) {
        return false;
    }

    return options.some(option => String(option.value) === String(value));
};

const buildCurrentValueOption = (value, list = []) => {
    const lookup = list.find(element => String(element.code) === String(value));

    return {
        key: value,
        value,
        title: lookup?.description ?? value,
        isActive: lookup?.isActive
    };
};

const includeCurrentValueOption = (options, value, list = []) => {
    if (!hasMeaningfulValue(value) || hasValidSelectedOption(value, options)) {
        return options;
    }

    return [
        buildCurrentValueOption(value, list),
        ...options
    ];
};

const clearInvalidCulturalSelections = (ethnicity, columns) => {
    if (!ethnicity) {
        return {
            value: ethnicity,
            hasChanges: false
        };
    }

    let hasChanges = false;
    const sanitisedEthnicity = {...ethnicity};

    columns.forEach(column => {
        const currentValue = sanitisedEthnicity[column.key];
        const hasMeaningfulValue = currentValue !== null && currentValue !== undefined && currentValue !== '';

        if (hasMeaningfulValue && !hasValidSelectedOption(currentValue, column.options)) {
            sanitisedEthnicity[column.key] = null;
            hasChanges = true;
        }
    });

    return {
        value: hasChanges ? sanitisedEthnicity : ethnicity,
        hasChanges
    };
};

// Maria, 18 October 2026: a British national cannot have refugee or asylum status, whether the answer to the
// nationality question is British or a British passport has been added (passport details are optional)
export const isBritishNationality = description => /^\s*british\s*$/i.test(description ?? '');

const buildAsylymStatusOptions = (list, passports, nationalityDescription) => {
    return passports?.some(p => p.nationCode === 'GBR') || isBritishNationality(nationalityDescription)
        ? buildSelectOptions(list.filter(e => e.code === 'NOT_APPLICABLE'))
        : buildSelectOptions(list);
};

const isEngAdditionalLanguageOptions = [
    {
        code: 'false',
        description: 'No'
    },
    {
        code: 'true',
        description: 'Yes'
    },
];

// Maria, 18 October 2026: pop-ups on the questions that need explaining, with the Department for Education's wording
const QUESTION_HELP = {
    ethnicityCode: 'Ethnicity is the ethnic group the parent or student identifies with. Select ‘Refused’ if you prefer not to say.',
    firstLanguageCode: 'The language the child was first exposed to during early development and continues to be exposed to at home or in the community.',
    isEnglishAdditional: 'The Department for Education defines English as an Additional Language as ‘Any pupil exposed to a language at home that is known or believed to be other than English’.',
    // Maria, 18 October 2026: why the answer is set for them
    asylymStatusCode: 'A British national cannot have refugee or asylum status, so this is ‘Not Applicable’ when the nationality is British or a British passport has been added.'
};

const EthnicityFields = ({readonly}) => {
    const {
        selectedProfile,
        updatingSuccessful,
        ethnicityLookups,
        currentSchool
    } = useSelector(state => state.profilesInfo);
    const {studentId} = useParams();
    const [ethnicity, setEthnicity] = useState({});
    const [isEdited, setIsEdited] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const dispatch = useDispatch();
    const [passports, setPassports] = useState([]);
    const [deletedPassportIds, setDeletedPassportIds] = useState([]);
    const studentName = selectedProfile.name
        ? selectedProfile.name
        : '';

    useEffect(() => {
        dispatch(retrieveEthnicityLookups()).then(() => {
            dispatch(retrieveEthnicity({studentId}));
        });
    }, []);

    useEffect(() => {
        if (selectedProfile.ethnicityData) {
            setPassports(selectedProfile.ethnicityData.nationalityPassport);
            setEthnicity(selectedProfile.ethnicityData);
        }
    }, [selectedProfile.ethnicityData]);

    useEffect(() => {
        if (updatingSuccessful === undefined) {
            return;
        }

        const syncAfterUpdate = async () => {
            showNotification(updatingSuccessful);

            if (updatingSuccessful) {
                await Promise.all([
                    dispatch(retrieveEthnicity({studentId})),
                    dispatch(retrieveProfile({studentId}))
                ]);
            }

            setIsSaving(false);
            dispatch(resetUpdatingStatus());
        };

        syncAfterUpdate();
    }, [dispatch, studentId, updatingSuccessful]);

    const ethnicityOptions = useMemo(
        () => buildSelectOptions(ethnicityLookups?.ethnicities ?? []),
        [ethnicityLookups?.ethnicities]
    );

    const religionOptions = useMemo(
        () => buildSelectOptions(ethnicityLookups?.religions ?? []),
        [ethnicityLookups?.religions]
    );

    const languageOptions = useMemo(
        () => buildSelectOptions(ethnicityLookups?.languages ?? [], ['English']),
        [ethnicityLookups?.languages]
    );

    const firstLanguageLookups = useMemo(() => {
        const firstLanguages = ethnicityLookups?.firstLanguages ?? [];

        return firstLanguages.length > 0
            ? firstLanguages
            : ethnicityLookups?.languages ?? [];
    }, [ethnicityLookups?.firstLanguages, ethnicityLookups?.languages]);

    const firstLanguageOptions = useMemo(() => {
        const shouldFilterInactive = firstLanguageLookups.some(language => language.isActive);
        const optionsSource = shouldFilterInactive
            ? firstLanguageLookups.filter(language => language.isActive)
            : firstLanguageLookups;

        return includeCurrentValueOption(
            buildSelectOptions(optionsSource, ['English']),
            ethnicity.firstLanguageCode,
            firstLanguageLookups
        );
    }, [
        ethnicity.firstLanguageCode,
        firstLanguageLookups
    ]);

    useEffect(() => {
        const firstLanguages = ethnicityLookups?.firstLanguages ?? [];
        const fallbackLanguages = ethnicityLookups?.languages ?? [];
        const activeCount = firstLanguageLookups.filter(language => language.isActive).length;

        console.info('[Cultural] First Language lookups', {
            source: firstLanguages.length > 0 ? 'firstLanguages' : 'languages fallback',
            firstLanguagesCount: firstLanguages.length,
            fallbackLanguagesCount: fallbackLanguages.length,
            lookupCount: firstLanguageLookups.length,
            activeCount,
            shouldFilterInactive: activeCount > 0,
            optionCount: firstLanguageOptions.length,
            selectedFirstLanguageCode: ethnicity.firstLanguageCode,
            lookups: firstLanguageLookups,
            options: firstLanguageOptions
        });
    }, [
        ethnicity.firstLanguageCode,
        ethnicityLookups?.firstLanguages,
        ethnicityLookups?.languages,
        firstLanguageLookups,
        firstLanguageOptions
    ]);

    const englishAdditionalLanguageOptions = useMemo(
        () => buildSelectOptions(isEngAdditionalLanguageOptions),
        []
    );

    const asylumStatusOptions = useMemo(
        () => buildAsylymStatusOptions(
            ethnicityLookups?.asylymStatuses ?? [],
            passports,
            (ethnicityLookups?.nationalIdentities ?? []).find(n => String(n.code) === String(ethnicity.nationalIdentityCode))?.description
        ),
        [ethnicityLookups?.asylymStatuses, ethnicityLookups?.nationalIdentities, ethnicity.nationalIdentityCode, passports]
    );

    const countryOfBirthOptions = useMemo(
        () => buildSelectOptions(ethnicityLookups?.nations ?? [], ['United Kingdom']),
        [ethnicityLookups?.nations]
    );

    const nationalityOptions = useMemo(
        () => buildSelectOptions(ethnicityLookups?.nationalIdentities ?? []),
        [ethnicityLookups?.nationalIdentities]
    );

    // Maria, 18 October 2026: three to a row - the languages one under the other, and nationality directly
    // above the refugee or asylum question
    const columns = useMemo(() => [
        {
            description: `What is ${studentName}’s ethnicity?`,
            value: ethnicity.ethnicityCode,
            options: ethnicityOptions,
            key: 'ethnicityCode',
            required: true
        },
        {
            description: `What is ${studentName}’s religion?`,
            value: ethnicity.religionCode,
            options: religionOptions,
            key: 'religionCode',
            required: true
        },
        {
            description: `What is ${studentName}’s country of birth?`,
            value: ethnicity.countryOfBirthCode,
            options: countryOfBirthOptions,
            key: 'countryOfBirthCode',
            required: true
        },
        {
            description: `What was the first language ${studentName} was exposed to?`,
            value: ethnicity.firstLanguageCode,
            options: firstLanguageOptions,
            key: 'firstLanguageCode',
            required: true
        },
        {
            description: `What is ${studentName}’s nationality?`,
            value: ethnicity.nationalIdentityCode,
            options: nationalityOptions,
            key: 'nationalIdentityCode',
            required: true
        },
        {
            description: `Does ${studentName} speak English as an additional language?`,
            value: ethnicity.isEnglishAdditional,
            options: englishAdditionalLanguageOptions,
            key: 'isEnglishAdditional',
            required: true
        },
        {
            description: `What language does ${studentName} speak at home?`,
            value: ethnicity.homeLanguageCode,
            options: languageOptions,
            key: 'homeLanguageCode',
            required: true
        },
        {
            description: `Does ${studentName} have refugee or asylum status?`,
            value: ethnicity.asylymStatusCode || 'NOT_APPLICABLE',
            options: asylumStatusOptions,
            key: 'asylymStatusCode',
            required: true
        }
    ], [
        studentName,
        ethnicity.ethnicityCode,
        ethnicity.religionCode,
        ethnicity.firstLanguageCode,
        ethnicity.homeLanguageCode,
        ethnicity.isEnglishAdditional,
        ethnicity.asylymStatusCode,
        ethnicity.countryOfBirthCode,
        ethnicity.nationalIdentityCode,
        ethnicityOptions,
        religionOptions,
        firstLanguageOptions,
        languageOptions,
        englishAdditionalLanguageOptions,
        asylumStatusOptions,
        countryOfBirthOptions,
        nationalityOptions
    ]);

    useEffect(() => {
        if (!selectedProfile.ethnicityData || Object.keys(ethnicityLookups ?? {}).length === 0) {
            return;
        }

        setEthnicity(currentEthnicity => {
            const sanitisedEthnicity = clearInvalidCulturalSelections(currentEthnicity, columns);

            if (sanitisedEthnicity.hasChanges) {
                setIsEdited(true);
            }

            return sanitisedEthnicity.value;
        });
    }, [selectedProfile.ethnicityData, ethnicityLookups]);

    const showNotification = (success) => success !== undefined && dispatch({
        true: () => showSnackbar({
            message: 'Ethnicity updated successfully', severity: 'success'
        }),
        false: () => showSnackbar({
            message: 'Failed to update ethnicity', severity: 'error'
        })
    }[success]());

    const handleSave = async () => {
        const normalizePassportDates = (passport) => ({
            ...passport,
            issueDate: toDateOnly(passport.issueDate),
            expiryDate: toDateOnly(passport.expiryDate),
            visaExpiryDate: toDateOnly(passport.visaExpiryDate),
        });

        const newPassports = passports.filter(e => e.isNew).map(normalizePassportDates);
        const editedPassports = passports.filter(e => e.isEdited).map(normalizePassportDates);

        setIsSaving(true);

        await dispatch(updateEthnicity({
            ethnicity: isEdited ? ethnicity : null,
            studentId,
            newPassports,
            editedPassports,
            deletedPassportIds
        })).unwrap();

        setIsEdited(false);
        setDeletedPassportIds([]);
    };

    const handleChangeValue = (value, key) => {
        let updatedEthnicity = JSON.parse(JSON.stringify(ethnicity));
        if (key !== 'isEnglishAdditional') {
            updatedEthnicity[key] = value ? value : null;
            // Maria, 18 October 2026: choosing British sets refugee or asylum status to Not Applicable
            if (key === 'nationalIdentityCode' &&
                isBritishNationality((ethnicityLookups?.nationalIdentities ?? []).find(n => String(n.code) === String(value))?.description)) {
                updatedEthnicity.asylymStatusCode = 'NOT_APPLICABLE';
            }
        } else {
            updatedEthnicity.isEnglishAdditional = value.toString();
        }
        setEthnicity(updatedEthnicity);
        setIsEdited(true);
    };

    const isAllEthnicityFieldsFilled = () =>
        columns
            .filter(column => column.required)
            .every(column => hasValidSelectedOption(column.value, column.options));

    const hasEditedOrNewPassports = passports?.some(e => e.isEdited || e.isNew);
    const isEthnicityConfirmed = !!selectedProfile.ethnicityData?.confirmedAt;
    const isAnyPassportDeleted = deletedPassportIds.length > 0;

    return (
        <>
            {/* Maria, 18 October 2026: her wording, in place of the heading, above the white box as on Contacts. The
                English as an Additional Language definition is a pop-up on that question. */}
            <Typography variant="body1" sx={{mb: 1.5}}>
                The information in this section is required by the Department for Education. All questions in this
                section must be responded to. If you do not wish to provide answers to any of these questions, please
                select ‘Refused’ from the list of options.
            </Typography>
        <ConfirmationWrapper
            readonly={readonly}
            hideActions={selectedProfile.personType === PROFILE_TYPE.ENQUIRY}
            confirmData={{
                confirmMessage: `I confirm that the information in the Cultural section of ${selectedProfile.name} ${selectedProfile.surname}'s student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`,
                confirmedAt: selectedProfile.ethnicityData?.confirmedAt,
                confirmedBy: selectedProfile.ethnicityData?.confirmedBy,
                enableSaveButton: !isSaving && isAllEthnicityFieldsFilled() &&
                    (isEdited || hasEditedOrNewPassports || isAnyPassportDeleted),
                enableConfirmButton: !isSaving && !isEdited && !hasEditedOrNewPassports &&
                    !isAnyPassportDeleted && !isEthnicityConfirmed && isAllEthnicityFieldsFilled(),
                studentId,
                type: STUDENT_PROFILE_CONFIRMATION_TYPES.Cultural,
                confirmOnSave: true,
                handleSave,
                onConfirmSuccess: async () => {
                    await Promise.all([
                        dispatch(retrieveEthnicity({studentId})),
                        dispatch(retrieveProfile({studentId}))
                    ]);
                },
                confirmButtonText: 'Confirm Cultural Information'
            }}
        >
            {actions => <>
            {/* Maria, 18 October 2026: outlined boxes like the rest of the profile, two to a row and closer together,
                with 'Select an answer' in an unanswered box */}
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))'}, columnGap: 3, rowGap: 2, pt: 0.5, pb: 1}}>
                {columns && columns.map(column =>
                    <Box key={column.key}>
                        <Typography fontWeight="bold" variant="body1" sx={{mb: 0.5}}>
                            {QUESTION_HELP[column.key]
                                ? <ContactHelpTip inline label={`${column.description}${column.required ? ' *' : ''}`} text={QUESTION_HELP[column.key]}/>
                                : `${column.description}${column.required ? ' *' : ''}`}
                        </Typography>
                        <Select
                            fullWidth
                            size="small"
                            displayEmpty
                            value={column.value ?? ''}
                            onChange={event => handleChangeValue(event.target.value, column.key)}
                            disabled={readonly}
                            renderValue={value => (value === '' || value === null || value === undefined
                                ? <Box component="span" sx={{color: '#7a8796'}}>Select an answer</Box>
                                : column.options?.find(option => option.value === value)?.title ?? value)}
                            inputProps={{'aria-label': column.description}}
                        >
                            {column.options?.map(option => (
                                <MenuItem key={option.key ?? option.value ?? option.title} value={option.value} disabled={option.disabled}>
                                    {option.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                )}
                {/* Maria, 18 October 2026: the confirm button in the empty space after the last question on a wide
                    screen; underneath the passports on a smaller one */}
                <Box sx={{display: {xs: 'none', md: 'flex'}, alignItems: 'flex-end', justifyContent: 'flex-end'}} data-testid="cultural-confirm-beside">
                    {actions}
                </Box>
            </Box>
            {/* the passports end level with the second column of questions */}
            <Box sx={{width: {xs: '100%', md: 'calc((100% - 48px) * 2 / 3 + 24px)'}}}>
                <PassportFields
                        readonly={readonly}
                        passports={passports}
                        setPassports={setPassports}
                        countries={ethnicityLookups.nations}
                        setDeletedPassportIds={setDeletedPassportIds}
                        deletedPassportIds={deletedPassportIds}
                        asylymStatus={ethnicity.asylymStatusCode}
                        student={{
                            name: selectedProfile.name
                        }}
                    />
            </Box>
            <Box sx={{display: {xs: 'block', md: 'none'}}}>{actions}</Box>
            </>}
        </ConfirmationWrapper>
        </>
    );
};

EthnicityFields.propTypes = {
    readonly: PropTypes.bool
};

export default EthnicityFields;
